import { Routes, Route, Navigate } from 'react-router-dom';
import Layout from './components/Layout';
import AdminLayout from './components/AdminLayout';
import Dashboard from './pages/Dashboard';
import Updates from './pages/Updates';
import Users from './pages/Users';
import Hashtags from './pages/Hashtags';
import Settings from './pages/Settings';
import {
  AdminLogin,
  AdminDashboard,
  AdminUsers,
  AdminAuditLogs,
  AdminCompliance,
  AdminSimulator,
  AdminOrgStructure,
  AdminRollups,
  AdminSubmissions,
  AdminRollupLocks,
  AdminSettings,
  AdminEmployees,
  AdminCustomers,
  AdminProducts,
  AdminImport,
} from './pages/admin';
import { AdminAuthProvider, useAdminAuth } from './contexts/AdminAuthContext';
import MagicLinkLanding from './pages/MagicLinkLanding';
import RequestNewLink from './pages/RequestNewLink';

// Protected route wrapper for admin pages
function AdminProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAdminAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-gray-500">Loading...</div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/admin/login" replace />;
  }

  return <AdminLayout>{children}</AdminLayout>;
}

// Admin routes wrapper with auth provider
function AdminRoutes() {
  return (
    <AdminAuthProvider>
      <Routes>
        <Route path="/login" element={<AdminLogin />} />
        <Route
          path="/"
          element={
            <AdminProtectedRoute>
              <AdminDashboard />
            </AdminProtectedRoute>
          }
        />
        <Route
          path="/users"
          element={
            <AdminProtectedRoute>
              <AdminUsers />
            </AdminProtectedRoute>
          }
        />
        <Route
          path="/portal-admins"
          element={
            <AdminProtectedRoute>
              <AdminUsers />
            </AdminProtectedRoute>
          }
        />
        <Route
          path="/employees"
          element={
            <AdminProtectedRoute>
              <AdminEmployees />
            </AdminProtectedRoute>
          }
        />
        <Route
          path="/customers"
          element={
            <AdminProtectedRoute>
              <AdminCustomers />
            </AdminProtectedRoute>
          }
        />
        <Route
          path="/products"
          element={
            <AdminProtectedRoute>
              <AdminProducts />
            </AdminProtectedRoute>
          }
        />
        <Route
          path="/import"
          element={
            <AdminProtectedRoute>
              <AdminImport />
            </AdminProtectedRoute>
          }
        />
        <Route
          path="/rollups"
          element={
            <AdminProtectedRoute>
              <AdminRollups />
            </AdminProtectedRoute>
          }
        />
        <Route
          path="/submissions"
          element={
            <AdminProtectedRoute>
              <AdminSubmissions />
            </AdminProtectedRoute>
          }
        />
        <Route
          path="/rollup-locks"
          element={
            <AdminProtectedRoute>
              <AdminRollupLocks />
            </AdminProtectedRoute>
          }
        />
        <Route
          path="/audit"
          element={
            <AdminProtectedRoute>
              <AdminAuditLogs />
            </AdminProtectedRoute>
          }
        />
        <Route
          path="/compliance"
          element={
            <AdminProtectedRoute>
              <AdminCompliance />
            </AdminProtectedRoute>
          }
        />
        <Route
          path="/simulator"
          element={
            <AdminProtectedRoute>
              <AdminSimulator />
            </AdminProtectedRoute>
          }
        />
        {/* Placeholder routes for other admin pages */}
        <Route
          path="/orgs"
          element={
            <AdminProtectedRoute>
              <PlaceholderPage title="Organizations" />
            </AdminProtectedRoute>
          }
        />
        <Route
          path="/hierarchy"
          element={
            <AdminProtectedRoute>
              <AdminOrgStructure />
            </AdminProtectedRoute>
          }
        />
        <Route
          path="/org-chart"
          element={
            <AdminProtectedRoute>
              <AdminOrgStructure />
            </AdminProtectedRoute>
          }
        />
        <Route
          path="/ownership-rules"
          element={
            <AdminProtectedRoute>
              <PlaceholderPage title="Ownership Rules" />
            </AdminProtectedRoute>
          }
        />
        <Route
          path="/siem"
          element={
            <AdminProtectedRoute>
              <PlaceholderPage title="SIEM Webhooks" />
            </AdminProtectedRoute>
          }
        />
        <Route
          path="/retention"
          element={
            <AdminProtectedRoute>
              <PlaceholderPage title="Retention Policies" />
            </AdminProtectedRoute>
          }
        />
        <Route
          path="/settings"
          element={
            <AdminProtectedRoute>
              <AdminSettings />
            </AdminProtectedRoute>
          }
        />
      </Routes>
    </AdminAuthProvider>
  );
}

// Placeholder for pages not yet implemented
function PlaceholderPage({ title }: { title: string }) {
  return (
    <div>
      <h1 className="text-2xl font-bold text-gray-900 mb-4">{title}</h1>
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-8 text-center">
        <div className="text-4xl mb-4">🚧</div>
        <p className="text-gray-500">This page is coming soon</p>
      </div>
    </div>
  );
}

function App() {
  return (
    <Routes>
      {/* Magic-link landing (Phase 4 Action 44). Top-level so the URL
          customers receive in their invite email is clean:
            https://app.getpowermove.com/auth/magic?token=... */}
      <Route path="/auth/magic" element={<MagicLinkLanding />} />
      {/* Request-a-new-link landing (Phase 4 Action 45). */}
      <Route path="/auth/magic/request-new" element={<RequestNewLink />} />

      {/* Admin Portal Routes */}
      <Route path="/admin/*" element={<AdminRoutes />} />

      {/* Main App Routes */}
      <Route
        path="/*"
        element={
          <Layout>
            <Routes>
              <Route path="/" element={<Dashboard />} />
              <Route path="/updates" element={<Updates />} />
              <Route path="/users" element={<Users />} />
              <Route path="/hashtags" element={<Hashtags />} />
              <Route path="/settings" element={<Settings />} />
            </Routes>
          </Layout>
        }
      />
    </Routes>
  );
}

export default App;
